import type {EagColumn} from '../types/EagColumn';
import type {EagColumnOrGroup} from '../types/EagColumnGroup';
import {isEagColumnGroup} from './isEagColumnGroup';

/**
 * Prefix distinguishing group-header-row drag ids from plain column keys.
 * Column keys come from BE definitions and never contain this prefix, so a
 * `startsWith` check is enough to route drag events to the right handler.
 */
const GROUP_ROW_CELL_ID_PREFIX = 'eag-group-row:';

export const isGroupRowCellId = (id: string): boolean => id.startsWith(GROUP_ROW_CELL_ID_PREFIX);

export type GroupInfo = {groupId: string; headerName: string};

export type GroupRowCell =
  | {
      type: 'group';
      /** Drag/droppable id — unique per rendered run, never parsed (resolve via cellById). */
      id: string;
      groupId: string;
      headerName: string;
      /** Visible child column keys of this run, in render order. */
      columnKeys: string[];
    }
  | {
      type: 'ungrouped';
      id: string;
      columnKey: string;
    };

export type GroupRowModel = {
  /** All cells in render order — non-right-pinned runs first, right-pinned runs after. */
  cells: GroupRowCell[];
  /** Index in `cells` where the filler <th> is inserted (= count of non-right-pinned cells). */
  fillerIndex: number;
  cellById: Map<string, GroupRowCell>;
};

/**
 * Maps every grouped column key to its group's id and display label, in one
 * pass over the (definition-ordered) columnConfig. Ungrouped columns are absent.
 */
export const buildGroupInfoByKey = (columnConfig: EagColumnOrGroup[]): Map<string, GroupInfo> => {
  const infoByKey = new Map<string, GroupInfo>();
  for (const item of columnConfig) {
    if (isEagColumnGroup(item)) {
      for (const child of item.children) {
        infoByKey.set(child.key, {groupId: item.groupId, headerName: item.headerName});
      }
    }
  }
  return infoByKey;
};

type BuildGroupRowModelParams = {
  /** Visible non-right-pinned columns in render order. */
  nonRightPinnedColumns: EagColumn[];
  /** Visible right-pinned columns in render order. */
  rightPinnedColumns: EagColumn[];
  groupInfoByKey: Map<string, GroupInfo>;
};

/**
 * Builds the group-header row from the LIVE rendered column order: consecutive
 * columns sharing a groupId collapse into one `group` cell; every other column
 * becomes an `ungrouped` placeholder cell. The two pinned partitions are
 * chunked separately so a run never spans the filler <th> between them.
 *
 * The same groupId may produce multiple cells when its members are
 * non-consecutive in the live order (e.g. a member was dragged out); each run
 * gets a unique occurrence-suffixed id.
 */
export const buildGroupRowModel = (params: BuildGroupRowModelParams): GroupRowModel => {
  const occurrences = new Map<string, number>();
  const cells: GroupRowCell[] = [];

  const chunkPartition = (columns: EagColumn[]) => {
    // A run never merges with a cell from the previous partition — the filler
    // <th> sits between them, so same-group columns on both sides stay split.
    const partitionStart = cells.length;
    for (const column of columns) {
      const info = params.groupInfoByKey.get(column.key);
      if (!info) {
        cells.push({
          type: 'ungrouped',
          id: `${GROUP_ROW_CELL_ID_PREFIX}col:${column.key}`,
          columnKey: column.key,
        });
        continue;
      }
      const last = cells.length > partitionStart ? cells.at(-1) : undefined;
      if (last?.type === 'group' && last.groupId === info.groupId) {
        last.columnKeys.push(column.key);
        continue;
      }
      const occurrence = (occurrences.get(info.groupId) ?? 0) + 1;
      occurrences.set(info.groupId, occurrence);
      cells.push({
        type: 'group',
        id: `${GROUP_ROW_CELL_ID_PREFIX}${info.groupId}:${occurrence}`,
        groupId: info.groupId,
        headerName: info.headerName,
        columnKeys: [column.key],
      });
    }
  };

  chunkPartition(params.nonRightPinnedColumns);
  const fillerIndex = cells.length;
  chunkPartition(params.rightPinnedColumns);

  return {cells, fillerIndex, cellById: new Map(cells.map((cell) => [cell.id, cell]))};
};
